<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Label Management</h1>
            <p class="text-orange-100 text-lg">Create and manage categories, subcategories, and tags</p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="openAddLabelModal"
              :disabled="areAllLimitsReached"
              :class="[
                'px-6 py-3 backdrop-blur-sm rounded-xl transition-all duration-200',
                areAllLimitsReached 
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                  : 'bg-white bg-opacity-20 hover:bg-opacity-30 text-white'
              ]"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              {{ areAllLimitsReached ? 'All Limits Reached' : 'Add Label' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Warning when all limits reached -->
    <div v-if="areAllLimitsReached" class="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span class="text-yellow-800 font-medium">
          All label limits have been reached. You can edit or delete existing labels, but cannot create new ones.
        </span>
      </div>
    </div>

    <!-- Labels Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="type in ['category', 'subcategory', 'tag']" :key="type" class="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900 capitalize">{{ type }}</h3>
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-500">{{ labelsStore.groupedLabels[type]?.length || 0 }}/{{ getLimitForType(type) }}</span>
            <span :class="[
              'px-2 py-1 rounded-full text-xs font-medium',
              isLimitReached(type) ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            ]">
              {{ isLimitReached(type) ? 'Full' : 'Available' }}
            </span>
          </div>
        </div>
        
        <div class="space-y-2">
          <div v-for="label in labelsStore.groupedLabels[type]" :key="label.id" 
               class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
            <div class="flex items-center space-x-3">
              <span :class="[
                'w-3 h-3 rounded-full',
                type === 'category' ? 'bg-orange-500' :
                type === 'subcategory' ? 'bg-blue-500' :
                'bg-amber-600'
              ]"></span>
              <span class="font-medium text-gray-900">{{ label.name }}</span>
            </div>
            <div class="flex space-x-2">
              <button @click="editLabel(label)" 
                      class="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200">
                Edit
              </button>
              <button @click="deleteLabel(label.id)" 
                      class="text-red-600 hover:text-red-800 text-sm font-medium transition-colors duration-200">
                Delete
              </button>
            </div>
          </div>
          
          <div v-if="!labelsStore.groupedLabels[type] || labelsStore.groupedLabels[type].length === 0" 
               class="text-center py-6 text-gray-400">
            <svg class="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
            </svg>
            <p class="text-sm">No {{ type }} labels yet</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Label Modal -->
    <div v-if="showAddLabel || editingLabel" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm mx-4">
        <h3 class="text-lg font-semibold mb-4">{{ editingLabel ? 'Edit Label' : 'Add Label' }}</h3>
        <form @submit.prevent="saveLabel">
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Name</label>
            <input v-model="labelForm.name" required 
                   class="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Type</label>
            <select v-model="labelForm.type" required 
                    class="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="category">Category</option>
              <option value="subcategory">Subcategory</option>
              <option value="tag">Tag</option>
            </select>
          </div>
          <div class="flex justify-end space-x-2">
            <button type="button" @click="closeLabelModal" 
                    class="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 transition-colors duration-200">
              Cancel
            </button>
            <button type="submit" 
                    class="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors duration-200">
              {{ editingLabel ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog
      :visible="showDeleteDialog"
      title="Delete Label"
      message="Are you sure you want to delete this label? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      @confirm="handleDeleteLabelConfirm"
      @cancel="showDeleteDialog = false"
    />

    <!-- Limit Reached Dialog -->
    <ConfirmDialog
      :visible="showLimitDialog"
      title="Limit Reached"
      :message="limitDialogMessage"
      confirmText="OK"
      cancelText=""
      @confirm="closeLimitDialog"
      @cancel="closeLimitDialog"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useLabelsStore } from '../../stores/labels'
import { useNotificationStore } from '../../stores/notification'
import ConfirmDialog from '../../components/ConfirmDialog.vue'
import { PAGINATION_LIMITS, getLabelLimit, isLabelLimitReached } from '../../constants/pagination'

export default {
  name: 'AdminLabels',
  components: {
    ConfirmDialog
  },
  setup() {
    const labelsStore = useLabelsStore()
    const notificationStore = useNotificationStore()

    // Label management state
    const showAddLabel = ref(false)
    const editingLabel = ref(null)
    const labelForm = ref({ name: '', type: 'category' })
    const showDeleteDialog = ref(false)
    const labelToDelete = ref(null)
    const showLimitDialog = ref(false)
    const limitDialogMessage = ref('')

    const openAddLabelModal = () => {
      editingLabel.value = null
      labelForm.value = { name: '', type: 'category' }
      showAddLabel.value = true
    }

    const editLabel = (label) => {
      editingLabel.value = label
      labelForm.value = { name: label.name, type: label.type }
      showAddLabel.value = false
    }

    const closeLabelModal = () => {
      showAddLabel.value = false
      editingLabel.value = null
      labelForm.value = { name: '', type: 'category' }
    }

    const saveLabel = async () => {
      try {
        // Check limits before creating new labels
        if (!editingLabel.value) {
          const currentCount = labelsStore.groupedLabels[labelForm.value.type]?.length || 0
          
          if (isLabelLimitReached(labelForm.value.type, currentCount)) {
            const typeName = labelForm.value.type.charAt(0).toUpperCase() + labelForm.value.type.slice(1)
            const limit = getLabelLimit(labelForm.value.type)
            limitDialogMessage.value = `Maximum ${limit} ${typeName} labels allowed. Please delete an existing ${labelForm.value.type} label first.`
            showLimitDialog.value = true
            return
          }
        }

        if (editingLabel.value) {
          await labelsStore.updateLabel(editingLabel.value.id, labelForm.value)
          notificationStore.showSuccess('Label Updated', 'Label has been updated successfully')
        } else {
          await labelsStore.createLabel(labelForm.value)
          notificationStore.showSuccess('Label Created', 'Label has been created successfully')
        }
        await labelsStore.fetchLabelsGrouped()
        closeLabelModal()
      } catch (error) {
        console.error('Error saving label:', error)
        let message = error?.response?.data?.message || (error?.response?.data?.errors?.[0]?.msg) || error?.message || 'Failed to save label. Please try again.'
        
        // Handle specific backend errors
        if (error?.response?.data?.error === 'LimitReached') {
          limitDialogMessage.value = error.response.data.message
          showLimitDialog.value = true
        } else if (error?.response?.data?.error === 'DuplicateLabel') {
          limitDialogMessage.value = error.response.data.message
          showLimitDialog.value = true
        } else {
          notificationStore.showError('Error', message)
        }
      }
    }

    const deleteLabel = (id) => {
      labelToDelete.value = id
      showDeleteDialog.value = true
    }

    const handleDeleteLabelConfirm = async () => {
      if (labelToDelete.value) {
        await labelsStore.deleteLabel(labelToDelete.value)
        await labelsStore.fetchLabelsGrouped()
      }
      showDeleteDialog.value = false
      labelToDelete.value = null
    }

    const getLimitForType = (type) => {
      return getLabelLimit(type) || '∞'
    }

    const isLimitReached = (type) => {
      const currentCount = labelsStore.groupedLabels[type]?.length || 0
      return isLabelLimitReached(type, currentCount)
    }

    const areAllLimitsReached = computed(() => {
      return Object.keys(PAGINATION_LIMITS.LABELS).every(type => 
        isLabelLimitReached(type, labelsStore.groupedLabels[type]?.length || 0)
      )
    })

    const closeLimitDialog = () => {
      showLimitDialog.value = false
      limitDialogMessage.value = ''
    }

    onMounted(() => {
      labelsStore.fetchLabelsGrouped()
    })

    return {
      labelsStore,
      showAddLabel,
      editingLabel,
      labelForm,
      showDeleteDialog,
      labelToDelete,
      showLimitDialog,
      limitDialogMessage,
      openAddLabelModal,
      editLabel,
      closeLabelModal,
      saveLabel,
      deleteLabel,
      handleDeleteLabelConfirm,
      getLimitForType,
      isLimitReached,
      areAllLimitsReached,
      closeLimitDialog
    }
  }
}
</script> 